import React, { useState } from "react";
import { Edit, Eye, ToggleLeft, ToggleRight } from "lucide-react";
import { format, parseISO } from "date-fns";
import Badge from "../ui/badge/Badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../ui/table";
import { SubscriptionPlan } from "../../pages/SubscriptionPlans/types";

interface SubscriptionPlanTableProps {
  plans: SubscriptionPlan[];
  currentPage: number;
  totalPages: number;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onToggleStatus: (plan: SubscriptionPlan) => Promise<void>;
}

const formatBillingCycle = (interval: string) => {
  if (interval === "year") return "Yearly";
  if (interval === "month") return "Monthly";
  return interval || "N/A";
};

const formatCurrency = (price: number, currency: string) => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "usd").toUpperCase(),
    }).format(price || 0);
  } catch {
    return `${price || 0} ${(currency || "").toUpperCase()}`;
  }
};

const formatDate = (date?: string) => {
  if (!date) return "N/A";
  try {
    return format(parseISO(date), "MM/dd/yyyy hh:mm a");
  } catch {
    return date;
  }
};

export default function SubscriptionPlanTable({
  plans,
  currentPage,
  totalPages,
  isLoading,
  onPageChange,
  onToggleStatus,
}: SubscriptionPlanTableProps) {
  const [updatingId, setUpdatingId] = useState("");

  const handleToggle = async (plan: SubscriptionPlan) => {
    setUpdatingId(plan._id);
    try {
      await onToggleStatus(plan);
    } finally {
      setUpdatingId("");
    }
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
      <div className="max-w-full overflow-x-auto">
        <div className="min-w-[1200px]">
          <Table>
            <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
              <TableRow>
                {[
                  "Plan Name",
                  "Description",
                  "Price",
                  "Billing Cycle",
                  "Status",
                  "Apple Product ID",
                  "Google Product ID",
                  "Updated Date",
                  "Actions",
                ].map((header) => (
                  <TableCell
                    key={header}
                    isHeader
                    className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start"
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
              {isLoading ? (
                [...Array(5)].map((_, index) => (
                  <TableRow key={index}>
                    {[...Array(9)].map((__, cellIndex) => (
                      <TableCell key={cellIndex} className="px-5 py-4">
                        <div className="h-4 w-full animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : plans.length === 0 ? (
                <TableRow>
                  <td
                    colSpan={9}
                    className="px-5 py-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    No subscription plans found.
                  </td>
                </TableRow>
              ) : (
                plans.map((plan) => (
                  <TableRow key={plan._id}>
                    <TableCell className="px-5 py-4 text-gray-700 text-theme-sm dark:text-gray-300">
                      <div className="font-medium">{plan.name}</div>
                    </TableCell>
                    <TableCell className="px-5 py-4 text-gray-500 text-theme-sm dark:text-gray-400">
                      <div className="max-w-[260px] truncate" title={plan.description || ""}>
                        {plan.description || "N/A"}
                      </div>
                    </TableCell>
                    <TableCell className="px-5 py-4 text-gray-500 text-theme-sm dark:text-gray-400">
                      {formatCurrency(plan.price, plan.currency)}
                    </TableCell>
                    <TableCell className="px-5 py-4 text-gray-500 text-theme-sm dark:text-gray-400">
                      {formatBillingCycle(plan.interval)}
                    </TableCell>
                    <TableCell className="px-5 py-4 text-theme-sm">
                      <Badge color={plan.isActive ? "success" : "error"}>
                        {plan.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-5 py-4 text-gray-500 text-theme-sm dark:text-gray-400">
                      <div className="max-w-[220px] truncate" title={plan.appleProductId || ""}>
                        {plan.appleProductId || "N/A"}
                      </div>
                    </TableCell>
                    <TableCell className="px-5 py-4 text-gray-500 text-theme-sm dark:text-gray-400">
                      <div className="max-w-[220px] truncate" title={plan.googleProductId || ""}>
                        {plan.googleProductId || "N/A"}
                      </div>
                    </TableCell>
                    <TableCell className="px-5 py-4 text-gray-500 text-theme-sm dark:text-gray-400">
                      {formatDate(plan.updatedAt)}
                    </TableCell>
                    <TableCell className="px-5 py-4 text-gray-500 text-theme-sm dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            window.location.href = `/subscription-plans/${plan._id}`;
                          }}
                          className="p-2 text-gray-500 hover:bg-gray-100 rounded-md dark:hover:bg-gray-800"
                          title="View details"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => {
                            window.location.href = `/edit-subscription-plan/${plan._id}`;
                          }}
                          className="p-2 text-blue-500 hover:bg-blue-100 rounded-md"
                          title="Edit plan"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleToggle(plan)}
                          className="p-2 text-indigo-500 hover:bg-indigo-100 rounded-md disabled:opacity-50"
                          title={plan.isActive ? "Inactive Plan" : "Active Plan"}
                          disabled={updatingId === plan._id}
                        >
                          {plan.isActive ? (
                            <ToggleRight className="w-5 h-5" />
                          ) : (
                            <ToggleLeft className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {plans.length !== 0 && totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 dark:bg-white/[0.03] dark:border-white/[0.05]">
          <div className="flex flex-1 justify-center">
            <nav className="relative z-0 inline-flex -space-x-px rounded-md" aria-label="Pagination">
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-medium border border-gray-300 ${
                    currentPage === page
                      ? "z-10 bg-indigo-600 text-white"
                      : "text-gray-500 bg-white hover:bg-gray-50"
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Next
              </button>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
}
